import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, fetchJson } from "@/lib/queryClient";
import type { FaixaAderencia, UnidadeLeitura } from "@shared/irrigacao";

// Espelha o que server/routes/manejo/irrigacao.ts devolve. Não importamos do server:
// o client não depende de módulos de lá.

export interface RegistroSetor {
  setorId: string;
  setorNome: string;
  culturaNome: string | null;
  unidade: UnidadeLeitura;
  unidadeRotulo: string;
  runtimeS: number;
  volumeM3: number;
  vazaoMediaM3h: number | null;
  areaHa: number | null;
  plantas: number | null;
  leitura: number | null;
  leituraPorHora: number | null;
  /** Nome da estação quando o setor a divide com outro — a lâmina dos dois é a mesma. */
  divideEstacaoCom: string | null;
  /** "L1 + L2" quando o setor é regado por estações que abrem juntas: o tempo é o da mais longa. */
  estacoesEmParalelo: string | null;
}

export interface DiaDeIrrigacao {
  leituraId: string;
  data: string;
  revisao: number;
  status: "ok" | "divergente" | "rejeitada";
  motivo: string | null;
  origem: "upload" | "email";
  recebidoEm: string;
  janelaInicio: string | null;
  janelaFim: string | null;
  volumeMedidoM3: number | null;
  volumeEstimadoTotalM3: number;
  fatorCalibracao: number | null;
  manutencao: boolean;
  manutencaoMotivo: string | null;
  setoresComRegistro: number;
  semIrrigacao: string[];
  registros: RegistroSetor[];
}

export interface DocumentoRecusado {
  leituraId: string;
  data: string;
  recebidoEm: string;
  origem: "upload" | "email";
  arquivoNome: string | null;
  motivo: string | null;
}

export interface RegistrosIrrigacao {
  de: string;
  ate: string;
  dias: DiaDeIrrigacao[];
  recusados: DocumentoRecusado[];
}

export interface EstacaoIrrigacao {
  id: string;
  numero: number;
  nome: string;
  ativo: boolean;
  /** Abre ao mesmo tempo que as outras simultâneas do mesmo setor. */
  simultanea: boolean;
  vazaoM3h: number | null;
  vazaoConfirmada: boolean;
  vazaoAtualizadaEm: string | null;
  vazaoObservacao: string | null;
  setorIds: string[];
  /** Sugestão por semelhança de nome. Sugestão — quem confirma é gente. */
  setorIdsSugeridos: string[];
}

export interface ConfiguracaoIrrigacao {
  conector: {
    provider: string;
    rotulo: string;
    /** DERIVADO da organização — somente leitura. O cliente nunca escolhe. */
    enderecoRecebimento: string | null;
    dominioInbound: string;
    remetenteEsperado: string | null;
    recebimentoAtivo: boolean;
  };
  setores: Array<{ id: string; nome: string }>;
  estacoes: EstacaoIrrigacao[];
  /** Estações sem vínculo que têm sugestão pronta — o que o botão em bloco resolve. */
  sugestoesPendentes: number;
  /** Estações sem vínculo e sem sugestão — essas continuam exigindo o diálogo. */
  semSugestao: number;
}

export interface ResultadoIngestao {
  status: "ok" | "divergente" | "rejeitada" | "ignorada";
  leituraId: string | null;
  dataReferencia: string | null;
  revisao: number | null;
  motivos: string[];
  setores: number;
  estacoes: number;
  estacoesDescobertas: number;
  vazoesSemeadas: number;
}

const CHAVE_REGISTROS = "/api/manejo/irrigacao/registros";
const CHAVE_ESTACOES = "/api/manejo/irrigacao/estacoes";

export function useRegistrosIrrigacao(periodo: { de?: string; ate?: string }) {
  const params = new URLSearchParams();
  if (periodo.de) params.set("de", periodo.de);
  if (periodo.ate) params.set("ate", periodo.ate);
  const qs = params.toString();
  return useQuery<RegistrosIrrigacao>({
    queryKey: [CHAVE_REGISTROS, periodo.de ?? "", periodo.ate ?? ""],
    // fetchJson checa r.ok — sem isso um 500 viraria objeto de erro tratado como dado.
    queryFn: () => fetchJson<RegistrosIrrigacao>(qs ? `${CHAVE_REGISTROS}?${qs}` : CHAVE_REGISTROS),
  });
}

export function useConfiguracaoIrrigacao() {
  return useQuery<ConfiguracaoIrrigacao>({
    queryKey: [CHAVE_ESTACOES],
    queryFn: () => fetchJson<ConfiguracaoIrrigacao>(CHAVE_ESTACOES),
  });
}

export function useEnviarRelatorio() {
  const qc = useQueryClient();
  return useMutation<ResultadoIngestao, Error, File>({
    mutationFn: async (arquivo: File) => {
      const corpo = new FormData();
      corpo.append("file", arquivo);
      // FormData não passa por apiRequest (que serializa JSON) — fetch direto, mas com
      // a MESMA checagem de status: sem ela um 400 viraria "sucesso" na tela.
      const resposta = await fetch("/api/manejo/irrigacao/upload", {
        method: "POST", body: corpo, credentials: "include",
      });
      if (!resposta.ok) {
        const texto = (await resposta.text()) || resposta.statusText;
        let mensagem = texto;
        try { mensagem = JSON.parse(texto).error ?? texto; } catch { /* corpo não-JSON: usa o texto cru */ }
        throw new Error(mensagem);
      }
      return resposta.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_REGISTROS] });
      qc.invalidateQueries({ queryKey: [CHAVE_ESTACOES] });
    },
  });
}

export function useSalvarVazao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ estacaoId, vazaoM3h, observacao }: { estacaoId: string; vazaoM3h: string; observacao?: string | null }) =>
      apiRequest("PUT", `/api/manejo/irrigacao/estacoes/${estacaoId}/vazao`, { vazaoM3h, observacao }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_ESTACOES] });
      qc.invalidateQueries({ queryKey: [CHAVE_REGISTROS] });
    },
  });
}

export function useSalvarSetoresDaEstacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ estacaoId, setorIds, simultanea }: { estacaoId: string; setorIds: string[]; simultanea: boolean }) =>
      apiRequest("PUT", `/api/manejo/irrigacao/estacoes/${estacaoId}/setores`, { setorIds, simultanea }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CHAVE_ESTACOES] }),
  });
}

/**
 * Salva a config da fonte.
 *
 * ⚠️ Só manda o que mudou, e o endereço NÃO vai no corpo: ele é derivado da organização
 * no servidor. Chave ausente preserva o valor salvo — mandar `null` para tudo era o que
 * fazia um salvamento parcial zerar o outro campo.
 */
export function useSalvarConectorIrrigacao() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dados: { remetenteEsperado?: string | null; recebimentoAtivo?: boolean }) =>
      apiRequest("PUT", "/api/manejo/irrigacao/conector", dados).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: [CHAVE_ESTACOES] }),
  });
}

/**
 * Aceita as sugestões de mapeamento em bloco.
 *
 * A sugestão continua nunca se aplicando sozinha na ingestão — isto é o clique humano,
 * só que um em vez de catorze. Foi a falta dele que travou o preview do ciclo 1.
 */
export function useAceitarSugestoesMapeamento() {
  const qc = useQueryClient();
  return useMutation<{ aplicadas: number; semSugestao: number }, Error, void>({
    mutationFn: () =>
      apiRequest("POST", "/api/manejo/irrigacao/estacoes/mapeamento-sugerido", {}).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_ESTACOES] });
      qc.invalidateQueries({ queryKey: [CHAVE_REGISTROS] });
    },
  });
}


// ─── Metas e Dashboard (ciclo 2) ─────────────────────────────────────────────

export interface SetorDaGrade {
  id: string;
  nome: string;
  culturaNome: string | null;
  unidade: UnidadeLeitura;
  unidadeRotulo: string;
}

export interface CelulaDeMeta {
  setorId: string;
  mes: number;
  valor: number;
  vigenciaInicio: string;
}

export interface GradeDeMetas {
  setores: SetorDaGrade[];
  metas: CelulaDeMeta[];
}

export interface BarraDeSetor {
  setorId: string;
  nome: string;
  culturaNome: string | null;
  unidade: UnidadeLeitura;
  unidadeRotulo: string;
  aplicado: number;
  /** A parte de `aplicado` que caiu em dias com meta — é ela que a aderência compara. */
  aplicadoComparavel: number;
  meta: number | null;
  metaParcial: boolean;
  aderencia: number | null;
  faixa: FaixaAderencia | null;
  diasComLeitura: number;
  volumeM3: number;
}

export interface PontoDaSerie {
  data: string;
  porUnidade: Record<UnidadeLeitura, number | null>;
  semLeitura: boolean;
  recusado: boolean;
  divergente: boolean;
  manutencao: boolean;
}

export interface ResumoPorUnidade {
  unidade: UnidadeLeitura;
  unidadeRotulo: string;
  laminaMediaDiaria: number | null;
  aderenciaMedia: number | null;
  setores: number;
  diasComLeitura: number;
}

export interface DashboardIrrigacao {
  de: string;
  ate: string;
  cobertura: { diasComLeitura: number; diasDoPeriodo: number; diasRecusados: number; diasEmManutencao: number };
  porUnidade: ResumoPorUnidade[];
  aguaAplicadaM3: number;
  barras: BarraDeSetor[];
  serie: PontoDaSerie[];
  heatmap: Array<{ setorId: string; data: string; valor: number; aderencia: number | null; faixa: FaixaAderencia | null }>;
  diasDivergentes: string[];
  culturas: string[];
  culturaSelecionada: string | null;
  anterior: { de: string; ate: string; porUnidade: ResumoPorUnidade[]; aguaAplicadaM3: number };
}

const CHAVE_METAS = "/api/manejo/irrigacao/metas";
const CHAVE_DASHBOARD = "/api/manejo/irrigacao/dashboard";

export function useGradeDeMetas() {
  return useQuery<GradeDeMetas>({
    queryKey: [CHAVE_METAS],
    queryFn: () => fetchJson<GradeDeMetas>(CHAVE_METAS),
  });
}

export function useSalvarMetas() {
  const qc = useQueryClient();
  return useMutation<{ gravadas: number }, Error, Array<{ setorId: string; mes: number; valor: string | null }>>({
    mutationFn: metas => apiRequest("PUT", CHAVE_METAS, { metas }).then(r => r.json()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [CHAVE_METAS] });
      qc.invalidateQueries({ queryKey: [CHAVE_DASHBOARD] });
    },
  });
}

export function useDashboardIrrigacao(filtro: { de: string; ate: string; cultura: string | null }) {
  const params = new URLSearchParams({ de: filtro.de, ate: filtro.ate });
  if (filtro.cultura) params.set("cultura", filtro.cultura);
  return useQuery<DashboardIrrigacao>({
    queryKey: [CHAVE_DASHBOARD, filtro.de, filtro.ate, filtro.cultura ?? ""],
    queryFn: () => fetchJson<DashboardIrrigacao>(`${CHAVE_DASHBOARD}?${params.toString()}`),
  });
}
